import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonButton from 'components/commonButton/CommonButton';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearRateCardsV2,
  getRateCardsV2,
  getReservationDetails,
} from 'redux/actions/reservationAction';
import { getDutyType } from 'redux/actions/UserDutyType';
import { getCar } from 'redux/actions/UserCar';
import { editDutySlipV2Service, previewDutySlipV2Service } from 'services/DutySlipV2';
import {
  blankRateFor,
  cardLabel,
  cardOntoTrip,
  clone,
  fieldVisible,
  fieldsFor,
  getAt,
  sameRates,
  setAt,
} from './rateShapes';
import DayLog from './DayLog';
import { fareRows } from './fareRows';

// Edit Duty Slip, v2 (2026-09-26). Built from the four agreed mockups.
//
// Two pricing modes: Rate Cards (defaultRate — the corporate's card, untouched,
// prices the trip and its id is stored) and Edited Rates (customRate — figures
// held on this trip alone, no card id). Switching mode never moves the fare;
// only picking a card or editing a figure does. Typing over a card's figures
// is the act of departing from it: the mode moves to Edited Rates and the
// control becomes the "Custom Rate Card" label. A trip saved on a card whose
// figures have since changed OPENS as Edited Rates — its figures no longer
// match any card — and any save records them as the trip's own.
//
// A multi-day rental is billed day by day, so its times, distances and
// odometer readings live on the day log (DayLog.js) instead of the trip-level
// fields, and each day is corrected on its own.
//
// The fare shown is always the backend's preview. Nothing here adds rupees.

const MODE_LABEL = { defaultRate: 'Rate Cards', customRate: 'Edited Rates' };
const DUTY_LABEL = { 4: 'Airport Transfer', 5: 'Rental', 7: 'Outstation' };
const TRIP_FACT_KEYS = [
  'tripStartTime',
  'tripEndTime',
  'odometerStart',
  'odometerEnd',
  'distanceSource',
  'garageToPickup',
  'dropToGarage',
  'tollCharges',
  'parkingCharges',
  'tripStartLocationManual',
  'tripEndLocationManual',
];
// On a multi-day rental these live on the day log, not on the trip.
const PER_DAY_KEYS = [
  'tripStartTime',
  'tripEndTime',
  'odometerStart',
  'odometerEnd',
  'distanceSource',
];

const formatDateTimeLocal = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
};
// The time pickers hold minutes while stored instants hold seconds; compare
// at the picker's resolution so an untouched time is never re-sent.
const toMinuteIso = (d) => {
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return '';
  return new Date(Math.floor(t / 60000) * 60000).toISOString();
};
const str = (v) => (v === null || v === undefined ? '' : `${v}`);
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  `₹ ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const readMode = (m) => (m === 'defaultRate' ? 'defaultRate' : 'customRate');

// Airport pricing refuses a drop it cannot resolve to a serviceable city — a
// check no other duty type applies, because only an airport transfer is assumed
// to stay local. Reached by changing the duty type, the engine's own wording
// reads as a system fault: it names neither the duty type that imposed the rule
// nor the trip that broke it. Until the modal can test this before the operator
// fills the form in, the message at least says what happened.
const explainPreviewError = (message, dutyType, bookedDutyType) => {
  const text = `${message || ''}`.trim();
  if (!text) return 'Could not price this edit';
  if (/not serviceable/i.test(text) && Number(dutyType) === 4 && dutyType !== bookedDutyType) {
    return (
      'This trip’s drop is outside the cities served for airport transfers, so it cannot be ' +
      'priced as one. Set the duty type back, or pick one that allows the drop.'
    );
  }
  return text;
};

// What each charge measures in. The fare engine will not price a charge whose
// unitType is missing — it returns zero with no message — and a charge the rate
// card never carried reaches the form as a bare { rate }, because that is the
// only key the form edits. These are the values the rate-card form itself
// writes, so a figure typed here prices the same as one configured there.
const DEFAULT_UNIT_TYPE = {
  driverAllowance: 'Rs',
  nightAllowance: 'Rs',
  earlyStartAllowance: 'Rs',
  extraHrs: 'Hour',
  extraKM: 'KM',
  waitingTime: 'Min',
};

// The rate figures sit on a tinted panel, and an input with no background of
// its own lets that tint through — so an editable field looked washed out,
// close enough to a disabled one to be read as "you cannot type here". An
// editable field is white; a disabled one keeps the panel's tint, which is now
// the thing that tells them apart.
const RATE_FIELD_SX = {
  '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
  '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: 'transparent' },
};

// The slip had grown past a laptop viewport: at 100% zoom the Pricing row and
// the rate figures sat above the fold, so an operator editing the trip's facts
// could not see what was pricing them.
//
// The height is mostly TYPE, not padding. src/theme.js raises the whole app's
// scale — body2 is 16px where MUI ships 14, body1 18 where it ships 16 — so
// every label, value and caption here renders a size or two up, and forty
// fields at that scale do not fit a laptop. Shaving padding against 16px text
// achieves nothing; the type has to come down.
//
// Scoped to this dialog: the app's scale suits the pages it was chosen for,
// and a dense correction form is not one of them.
const COMPACT_SX = {
  '& .MuiInputBase-input': { fontSize: 13.5 },
  '& .MuiInputLabel-root': { fontSize: 13.5 },
  '& .MuiTypography-body2': { fontSize: 13.5 },
  '& .MuiTypography-caption': { fontSize: 11.5 },
  '& .MuiFormControlLabel-label': { fontSize: 13 },
  '& .MuiMenuItem-root': { fontSize: 13.5 },
  // The helper line under a field is mostly a blank spacer reserving room for
  // a message that usually is not there.
  '& .MuiFormHelperText-root': {
    fontSize: 11,
    lineHeight: 1.3,
    mt: 0.25,
    mb: 0,
    minHeight: 13,
  },
  '& .MuiOutlinedInput-input': { paddingTop: '7.5px', paddingBottom: '7.5px' },
  '& .MuiInputBase-inputSizeSmall': { paddingTop: '7.5px', paddingBottom: '7.5px' },
  '& .MuiAlert-root': { py: 0.25 },
  '& .MuiAlert-message': { py: 0.5, fontSize: 13 },
  // A heading row carries one short line; the default leading gives it the
  // height of two.
  '& .colorPrimary': { lineHeight: 1.35 },
  '& .MuiFormControlLabel-root': { marginTop: 0, marginBottom: 0 },
};

// One line of the fare break-up. A charge that came to nothing is still
// printed, with the reason beside it, so a zero reads as "checked, did not
// apply" rather than as something the engine forgot.
const FareRow = ({ row, strong, ruled }) => {
  const nil = !Number(row.amount);
  return (
    <TableRow>
      <TableCell
        sx={{
          borderTop: ruled ? '1px solid #e0e0e0' : undefined,
          borderBottom: 'none',
          py: 0.75,
          color: nil && !strong ? '#969696' : undefined,
        }}
      >
        <Typography component="span" variant="body2" className={strong ? 'fw600' : undefined}>
          {row.label}
        </Typography>
        {row.why && (
          <Typography
            component="span"
            variant="caption"
            sx={{ ml: 1, color: '#969696', fontStyle: 'italic' }}
          >
            {row.why}
          </Typography>
        )}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          borderTop: ruled ? '1px solid #e0e0e0' : undefined,
          borderBottom: 'none',
          py: 0.75,
          whiteSpace: 'nowrap',
          color: nil && !strong ? '#969696' : undefined,
        }}
      >
        <Typography component="span" variant="body2" className={strong ? 'fw600' : undefined}>
          {fmt(row.amount)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

// What the dialog opens on. The backend's dutySlipV2 key carries it; until
// that backend is deployed, derive the same thing from the v1 context with
// the read rule applied here.
const contextFrom = (tripDetails) => {
  if (tripDetails?.dutySlipV2) return tripDetails.dutySlipV2;
  const c = tripDetails?.dutySlipEditContext || {};
  const addInfo = tripDetails?.additionalInfo || {};
  const storedMode = addInfo?.pricingMode ?? tripDetails?.rateConfig?.pricingMode ?? null;
  const mode = readMode(storedMode);
  const storedId = c?.rateCardId ?? null;
  return {
    version: 1,
    dutyType: c.dutyType ?? tripDetails?.type ?? null,
    carTypeId: tripDetails?.carTypeId ?? null,
    pricingMode: mode,
    storedPricingMode: storedMode,
    rateCardId: mode === 'defaultRate' ? storedId : null,
    originRateCardId: storedId,
    rateConfig: c.rateConfig ?? tripDetails?.rateConfig ?? null,
    tripStartTime: c.tripStartTime ?? null,
    tripEndTime: c.tripEndTime ?? null,
    actualTravelTime: c.actualTravelTime ?? null,
    distance: c.distance ?? null,
    distanceSource: c.distanceSource ?? 'gps',
    gpsDistance: c.gpsDistance ?? null,
    odometerDistance: c.odometerDistance ?? null,
    odometerStart: c.odometerStart ?? null,
    odometerEnd: c.odometerEnd ?? null,
    garageToPickup: c.garageToPickup ?? null,
    dropToGarage: c.dropToGarage ?? null,
    tollCharges: c.tollCharges ?? null,
    parkingCharges: c.parkingCharges ?? null,
    tripStartLocationManual: c.tripStartLocationManual ?? null,
    tripEndLocationManual: c.tripEndLocationManual ?? null,
    remarks: c.remarks ?? null,
    isMultiDay: Number(tripDetails?.type) === 5 && !!addInfo?.isMultiDay,
    multiDay: null,
  };
};

const factsFrom = (ctx) => ({
  tripStartTime: formatDateTimeLocal(ctx.tripStartTime),
  tripEndTime: formatDateTimeLocal(ctx.tripEndTime),
  odometerStart: str(ctx.odometerStart),
  odometerEnd: str(ctx.odometerEnd),
  distanceSource: ctx.distanceSource === 'odometer' ? 'odometer' : 'gps',
  garageToPickup: str(ctx.garageToPickup),
  dropToGarage: str(ctx.dropToGarage),
  tollCharges: str(ctx.tollCharges ?? 0),
  parkingCharges: str(ctx.parkingCharges ?? 0),
  tripStartLocationManual: str(ctx.tripStartLocationManual),
  tripEndLocationManual: str(ctx.tripEndLocationManual),
});

// The day log as loaded: one editable record per day, keyed by day number.
const dayEditsFrom = (ctx) => {
  const out = {};
  (ctx?.multiDay?.days || []).forEach((d) => {
    out[Number(d?.day)] = {
      startTime: formatDateTimeLocal(d?.startTime),
      endTime: formatDateTimeLocal(d?.endTime),
      odometerStart: str(d?.odometer?.start),
      odometerEnd: str(d?.odometer?.end),
      distanceSource: d?.distanceSource === 'odometer' ? 'odometer' : 'gps',
    };
  });
  return out;
};

// Only the days that moved, and only the fields that moved on them.
const daysPayload = (ctx, edits) => {
  const base = dayEditsFrom(ctx);
  const out = [];
  Object.keys(edits || {}).forEach((k) => {
    const n = Number(k);
    const e = edits[k] || {};
    const b = base[n] || {};
    const d = { day: n };
    const dateMoved = (key) =>
      str(e[key]) !== str(b[key]) &&
      (e[key] ? toMinuteIso(e[key]) : '') !== (b[key] ? toMinuteIso(b[key]) : '');
    if (dateMoved('startTime'))
      d.startTime = e.startTime ? new Date(e.startTime).toISOString() : null;
    if (dateMoved('endTime')) d.endTime = e.endTime ? new Date(e.endTime).toISOString() : null;
    if (
      str(e.odometerStart) !== str(b.odometerStart) ||
      str(e.odometerEnd) !== str(b.odometerEnd)
    ) {
      d.odometer = {
        start: str(e.odometerStart) === '' ? null : Number(e.odometerStart),
        end: str(e.odometerEnd) === '' ? null : Number(e.odometerEnd),
      };
    }
    if ((e.distanceSource || 'gps') !== (b.distanceSource || 'gps'))
      d.distanceSource = e.distanceSource;
    if (Object.keys(d).length > 1) out.push(d);
  });
  return out;
};

const DutySlipDialogV2 = ({ open, handleClose, tripId, tripDetails, onSave }) => {
  const dispatch = useDispatch();
  const ctx = useMemo(() => contextFrom(tripDetails), [tripDetails]);
  const bookedDutyType = Number(ctx.dutyType);
  const multiDay = !!ctx.isMultiDay;
  const hasDayLog = multiDay && Array.isArray(ctx?.multiDay?.days);

  const masterDutyTypeList = useSelector((state) => state?.userDutyType?.dutyTypeList?.data);
  const cards = useSelector((state) => state?.rateCardsV2Reducer?.rateCardsV2List?.data);
  const cardsLoading = useSelector((state) => !!state?.rateCardsV2Reducer?.loading);
  const vehicles = useSelector((state) => state?.userCar?.userCar?.data);

  const cardList = useMemo(() => (Array.isArray(cards) ? cards : []), [cards]);
  const liveById = useCallback(
    (id) => (id ? cardList.find((c) => `${c.id}` === `${id}`) || null : null),
    [cardList],
  );

  // ---- state ----
  // The duty type a trip was booked as can be wrong — an airport transfer that
  // was really a rental. Correcting it is a pricing decision, so it lives with
  // the basis: changing it holds the old figures on screen, frozen, until a
  // card for the new type is picked. Nothing is wiped, so setting it back costs
  // nothing.
  const [dutyType, setDutyType] = useState(bookedDutyType);
  const [mode, setMode] = useState('customRate');
  const [picked, setPicked] = useState(null);
  const [pickTouched, setPickTouched] = useState(false);
  const [copy, setCopy] = useState(null);
  const [editedDirty, setEditedDirty] = useState(false);
  const [adjustedFrom, setAdjustedFrom] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [facts, setFacts] = useState(factsFrom(ctx));
  const [dayEdits, setDayEdits] = useState(dayEditsFrom(ctx));
  const [remarks, setRemarks] = useState('');
  const [openingBasis, setOpeningBasis] = useState(null);
  const [basisTouched, setBasisTouched] = useState(false);
  const [cardsRequested, setCardsRequested] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBreakup, setShowBreakup] = useState(false);
  // The rate figures are the tallest block in the slip and the one least often
  // edited: most corrections are to the trip's facts, and the figures are read
  // off the card. They start folded so the slip opens showing what is being
  // priced and what it came to, and open on a click when they are the thing
  // being corrected.
  const [showRates, setShowRates] = useState(false);
  const previewSeq = useRef(0);

  const savedFare = num(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount);

  const resetToSaved = useCallback(
    (basis) => {
      const b = basis || {
        mode: ctx.pricingMode,
        cardId: ctx.rateCardId,
        copy: ctx.rateConfig,
      };
      setMode(b.mode);
      setPicked(b.cardId ?? null);
      setPickTouched(false);
      setCopy(clone(b.copy) ?? null);
      setEditedDirty(false);
      setAdjustedFrom(null);
      setVehicle(ctx.carTypeId ?? null);
      setBasisTouched(false);
    },
    [ctx],
  );

  // ---- open / close ----
  useEffect(() => {
    if (!open) return;
    setOpeningBasis(null);
    setCardsRequested(false);
    setPreview(null);
    setPreviewError('');
    setRemarks('');
    setShowRates(false);
    setDutyType(bookedDutyType);
    setFacts(factsFrom(ctx));
    setDayEdits(dayEditsFrom(ctx));
    resetToSaved(null);
    dispatch(getDutyType());
    if (tripDetails?.city && bookedDutyType) {
      dispatch(getCar({ type: bookedDutyType, location: tripDetails.city, loader: false }));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) dispatch(clearRateCardsV2());
  }, [open, dispatch]);

  // Cards for the trip's city, duty type and the SELECTED vehicle. A vehicle
  // change fetches the other vehicle's cards.
  useEffect(() => {
    if (!open || !tripDetails?.companyId || !tripDetails?.city || !dutyType || !vehicle) return;
    setCardsRequested(true);
    dispatch(
      getRateCardsV2({
        companyId: tripDetails.companyId,
        location: tripDetails.city,
        type: dutyType,
        carTypeId: vehicle,
        // A trip converted to Rental is always a single-day one — the backend
        // refuses to make a trip multi-day here — so it must ask for single-day
        // cards. Sending nothing would offer the multi-day card too, and pricing
        // a single day on it is a fare nobody chose. A trip that already is a
        // rental keeps its own flag.
        ...(dutyType === 5 ? { isMultiDay: dutyType === bookedDutyType ? multiDay : false } : {}),
      }),
    );
  }, [
    open,
    tripDetails?.companyId,
    tripDetails?.city,
    dutyType,
    bookedDutyType,
    vehicle,
    multiDay,
    dispatch,
  ]);

  // The state the dialog opens in, settled once the trip's own vehicle's cards
  // are known: a card trip whose card has since changed opens as Edited Rates.
  useEffect(() => {
    if (!open || openingBasis) return;
    const saved = {
      mode: ctx.pricingMode,
      cardId: ctx.rateCardId,
      copy: ctx.rateConfig,
    };
    const needsCards = saved.mode === 'defaultRate' && !!saved.cardId;
    if (needsCards && !(cardsRequested && !cardsLoading)) return;
    if (needsCards && vehicle !== ctx.carTypeId) return;
    let basis = { ...saved, from: null, diverged: false };
    if (needsCards) {
      const live = liveById(saved.cardId);
      if (live && !sameRates(live, saved.copy)) {
        basis = {
          mode: 'customRate',
          cardId: null,
          copy: saved.copy,
          from: saved.cardId,
          diverged: true,
        };
      }
    }
    setOpeningBasis(basis);
    if (!basisTouched) resetToSaved(basis);
  }, [
    open,
    openingBasis,
    ctx,
    cardsRequested,
    cardsLoading,
    vehicle,
    liveById,
    basisTouched,
    resetToSaved,
  ]);

  // ---- derived ----
  const hasCards = cardList.length > 0;
  const modeOptions = hasCards ? ['defaultRate', 'customRate'] : ['customRate'];
  const dutyTypeChanged = dutyType !== bookedDutyType;
  useEffect(() => {
    // No cards for this vehicle: Rate Cards is withdrawn; fall back to the
    // trip's own figures rather than leaving an unusable mode selected.
    //
    // After a duty type change the trip's own figures are the WRONG SHAPE — an
    // airport copy has no package, a package copy no flat rate — so they cannot
    // be carried across. The rate fields start empty in the new type's shape and
    // the operator supplies them; what is kept is everything that is a fact
    // about the trip rather than a rate.
    if (open && cardsRequested && !cardsLoading && !hasCards && mode === 'defaultRate') {
      setMode('customRate');
      setPicked(null);
      setPickTouched(false);
      setCopy(
        dutyTypeChanged
          ? blankRateFor(dutyType, openingBasis?.copy ?? ctx.rateConfig)
          : (clone(openingBasis?.copy ?? ctx.rateConfig) ?? null),
      );
    }
  }, [
    open,
    cardsRequested,
    cardsLoading,
    hasCards,
    mode,
    openingBasis,
    ctx.rateConfig,
    dutyTypeChanged,
    dutyType,
  ]);

  const cardMatchesTrip = () => {
    if (!picked) return false;
    if (pickTouched) return true;
    if (vehicle !== ctx.carTypeId) return false;
    const live = liveById(picked);
    if (!live) return true;
    return sameRates(live, openingBasis?.copy ?? ctx.rateConfig);
  };

  const basis = openingBasis || {
    mode: ctx.pricingMode,
    cardId: ctx.rateCardId,
    copy: ctx.rateConfig,
  };
  const vehicleChanged = !!vehicle && `${vehicle}` !== `${ctx.carTypeId}`;
  // Between changing the duty type and choosing what to price it on, the basis
  // on screen belongs to the type the trip no longer is. It is held, not used:
  // no preview is asked for and nothing can be saved.
  const basisHeld = dutyTypeChanged && !picked && mode === 'defaultRate';
  const pricingMoved =
    vehicleChanged ||
    dutyTypeChanged ||
    mode !== basis.mode ||
    (mode === 'defaultRate' && `${picked ?? ''}` !== `${basis.cardId ?? ''}`) ||
    !sameRates(copy, basis.copy);

  const factKeys = multiDay
    ? TRIP_FACT_KEYS.filter((k) => !PER_DAY_KEYS.includes(k))
    : TRIP_FACT_KEYS;
  const dayChanges = multiDay ? daysPayload(ctx, dayEdits) : [];
  const factsChanged = () => {
    const f0 = factsFrom(ctx);
    return factKeys.some((k) => str(facts[k]) !== str(f0[k])) || dayChanges.length > 0;
  };
  const anythingChanged = () => pricingMoved || factsChanged() || remarks.trim() !== '';

  const buildPayload = useCallback(() => {
    const p = {};
    const f0 = factsFrom(ctx);
    if (!multiDay) {
      const dateChanged = (k) =>
        facts[k] && toMinuteIso(facts[k]) !== (f0[k] ? toMinuteIso(f0[k]) : '');
      if (dateChanged('tripStartTime'))
        p.tripStartTime = new Date(facts.tripStartTime).toISOString();
      if (dateChanged('tripEndTime')) p.tripEndTime = new Date(facts.tripEndTime).toISOString();
      ['odometerStart', 'odometerEnd'].forEach((k) => {
        if (str(facts[k]) !== str(f0[k]) && str(facts[k]) !== '') p[k] = Number(facts[k]);
      });
      if (facts.distanceSource !== f0.distanceSource) p.distanceSource = facts.distanceSource;
    }
    ['garageToPickup', 'dropToGarage', 'tollCharges', 'parkingCharges'].forEach((k) => {
      if (str(facts[k]) !== str(f0[k]) && str(facts[k]) !== '') p[k] = Number(facts[k]);
    });
    ['tripStartLocationManual', 'tripEndLocationManual'].forEach((k) => {
      if (str(facts[k]) !== str(f0[k])) p[k] = facts[k];
    });
    if (multiDay) {
      const days = daysPayload(ctx, dayEdits);
      if (days.length) p.days = days;
    }
    if (vehicleChanged) p.carTypeId = vehicle;
    // The backend refuses a duty type change that arrives without a basis, so
    // these two always travel together.
    if (dutyTypeChanged) p.dutyType = dutyType;
    if (pricingMoved) {
      p.pricingMode = mode;
      if (mode === 'defaultRate') {
        if (picked) p.rateCardId = picked;
      } else if (copy) {
        p.rateConfig = copy;
      }
    }
    if (remarks.trim()) p.remarks = remarks.trim();
    return p;
  }, [
    ctx,
    multiDay,
    facts,
    dayEdits,
    vehicleChanged,
    vehicle,
    dutyTypeChanged,
    dutyType,
    pricingMoved,
    mode,
    picked,
    copy,
    remarks,
  ]);

  // ---- fare: always the backend's preview ----
  const needsPick = basisHeld || (mode === 'defaultRate' && !cardMatchesTrip());
  const payloadKey = JSON.stringify(buildPayload());
  useEffect(() => {
    if (!open || !openingBasis || !tripId) return;
    if (needsPick) return; // nothing to price until a card is chosen
    const seq = ++previewSeq.current;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      const { remarks: _r, ...body } = JSON.parse(payloadKey);
      const res = await previewDutySlipV2Service(tripId, body);
      if (seq !== previewSeq.current) return;
      setPreviewing(false);
      if (res?.success) {
        setPreview(res.data);
        setPreviewError('');
      } else {
        setPreview(null);
        setPreviewError(explainPreviewError(res?.message, dutyType, bookedDutyType));
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [open, openingBasis, tripId, payloadKey, needsPick, dutyType, bookedDutyType]);

  // ---- handlers ----
  const markEdited = () => {
    setBasisTouched(true);
    if (mode === 'customRate') {
      setEditedDirty(true);
      return;
    }
    // Editing a figure while a card is selected: the trip is no longer priced
    // on that card. Move to Edited Rates, keep what was typed, drop the pick.
    const from = liveById(picked);
    setAdjustedFrom(from ? cardLabel(from) : null);
    setMode('customRate');
    setEditedDirty(true);
    setPicked(null);
    setPickTouched(false);
  };
  // A TextField hands back a string even when type="number", and the fare
  // engine adds these figures together with +. One string turns the whole sum
  // into concatenation ("316.65" + "100" + "30"), so a rate the operator typed
  // must reach the backend as a number. An empty box stays empty rather than
  // becoming 0, so a cleared field reads as "not set", not "free".
  const setRate = (path, value, numeric) => {
    const next =
      numeric && typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))
        ? Number(value)
        : value;
    setCopy((prev) => {
      let copyNext = setAt(prev || {}, path, next);
      // The per-km rate is stored twice: inside rateConfig, and in the outer
      // column the fare engine reads for a /km card. The form edits the inner
      // one, so mirror it out or the two drift apart — a rate changed here and
      // then priced as /km would use the stale outer figure.
      if (path.length === 2 && path[0] === 'rateConfig' && path[1] === 'base_rate_per_km') {
        copyNext = setAt(copyNext, ['baseRatePerKM'], Number(next) || 0);
      }
      // A charge the card never carried arrives here as a bare { rate }, and
      // the modifiers that read one refuse a charge whose unitType is missing
      // and return zero without saying so. Typing a driver allowance onto a
      // card that has none looked like it worked and changed nothing. The form
      // only ever edits the rate, so the unit comes with it.
      if (path.length === 3 && path[0] === 'rateConfig' && path[2] === 'rate') {
        const unit = DEFAULT_UNIT_TYPE[path[1]];
        const block = getAt(copyNext, [path[0], path[1]]);
        if (unit && block && typeof block === 'object' && !block.unitType) {
          copyNext = setAt(copyNext, [path[0], path[1], 'unitType'], unit);
        }
      }
      return copyNext;
    });
    markEdited();
  };
  const onModeChange = (next) => {
    setBasisTouched(true);
    if (next === 'defaultRate') {
      setMode('defaultRate');
      setPicked(null); // nothing selected; the fare must not move
      setPickTouched(false);
      return;
    }
    // Carry the figures on screen into Edited Rates.
    const from = liveById(picked);
    setAdjustedFrom(from && !sameRates(copy, basis.copy) ? cardLabel(from) : null);
    setEditedDirty(!sameRates(copy, basis.copy));
    setMode('customRate');
    setPicked(null);
    setPickTouched(false);
  };
  const onPickCard = (id) => {
    setBasisTouched(true);
    setPicked(id || null);
    setPickTouched(!!id);
    const live = liveById(id);
    // Merge, never replace: a card has rates but none of this trip's garage
    // legs or measured figures, and dropping those loses the garage charge.
    if (live) setCopy((prev) => cardOntoTrip(live, prev ?? ctx.rateConfig));
  };
  // Changing the duty type freezes the basis rather than clearing it: the old
  // card and its figures stay on screen, un-editable, until a card for the new
  // type is picked. A card of the old type cannot stay selected — it would
  // price the trip as the shape it no longer is.
  const onDutyTypeChange = (next) => {
    const value = Number(next);
    if (value === dutyType) return;
    setBasisTouched(true);
    setDutyType(value);
    setPicked(null);
    setPickTouched(false);
    setAdjustedFrom(null);
    setEditedDirty(false);
    setMode('defaultRate');
    if (tripDetails?.city) {
      dispatch(getCar({ type: value, location: tripDetails.city, loader: false }));
    }
  };
  const onVehicleChange = (id) => {
    setBasisTouched(true);
    setVehicle(id);
    setPicked(null);
    setPickTouched(false);
    if (mode === 'defaultRate') {
      // Falls back to the trip's own figures until a card is picked for the
      // new vehicle. In Edited Rates the figures are the trip's own already.
      setAdjustedFrom(null);
      setCopy(clone(basis.copy) ?? null);
    }
  };
  // Kept for when the two undo links return with a scope each worth naming;
  // nothing calls it while they are withdrawn.
  // eslint-disable-next-line no-unused-vars
  const undoPricing = () => resetToSaved(openingBasis);
  const resetAll = () => {
    setDutyType(bookedDutyType);
    setFacts(factsFrom(ctx));
    setDayEdits(dayEditsFrom(ctx));
    setRemarks('');
    resetToSaved(openingBasis);
  };
  const setFact = (k, v) => setFacts((prev) => ({ ...prev, [k]: v }));
  const setDayField = (dayNo, key, value) =>
    setDayEdits((prev) => ({
      ...prev,
      [dayNo]: { ...(prev[dayNo] || {}), [key]: value },
    }));

  const onUpdate = async () => {
    const payload = buildPayload();
    if (!payload.remarks) {
      toast.error('Correction remarks are required');
      return;
    }
    setSaving(true);
    const res = await editDutySlipV2Service(tripId, payload);
    setSaving(false);
    if (!res?.success) {
      toast.error(res?.message || 'Failed to update the duty slip');
      return;
    }
    toast.success(res?.message || 'Duty slip updated');
    if (onSave) onSave();
    else dispatch(getReservationDetails(tripId));
    handleClose(false);
  };

  // ---- presentation helpers ----
  const labelForDuty = (value) =>
    (Array.isArray(masterDutyTypeList) &&
      masterDutyTypeList.find((d) => Number(d?.dutyTypeId) === Number(value))?.name) ||
    DUTY_LABEL[value] ||
    'Duty type';
  const dutyLabel = multiDay ? 'Rental, multi-day' : labelForDuty(dutyType);
  // A multi-day rental's fare is a day log the driver builds by pausing and
  // resuming; no other duty type has anywhere to put it, so its type is fixed.
  const dutyTypeOptions = useMemo(
    () => (multiDay ? [] : [4, 5, 7].map((v) => ({ value: v, label: labelForDuty(v) }))),
    [multiDay, masterDutyTypeList], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const vehicleOptions = useMemo(() => {
    const list = Array.isArray(vehicles)
      ? vehicles.map((v) => ({ id: v?.id, label: v?.carType }))
      : [];
    if (ctx.carTypeId && !list.some((v) => `${v.id}` === `${ctx.carTypeId}`)) {
      list.unshift({
        id: ctx.carTypeId,
        label: tripDetails?.carType?.carType || 'Current vehicle',
      });
    }
    return list;
  }, [vehicles, ctx.carTypeId, tripDetails?.carType?.carType]);
  const vehicleLabel = (id) =>
    vehicleOptions.find((v) => `${v.id}` === `${id}`)?.label || 'the vehicle';

  // The figures on screen no longer belong to what is being priced: either the
  // vehicle moved and no card has been picked for it, or the duty type moved
  // and the basis is held. Either way they are shown, frozen, not used.
  const stale = basisHeld || (mode === 'defaultRate' && vehicleChanged && !picked);
  const liveAnnotate =
    !editedDirty && vehicle === ctx.carTypeId
      ? mode === 'defaultRate' && picked && !pickTouched
        ? liveById(picked)
        : mode === 'customRate' && openingBasis?.diverged
          ? liveById(openingBasis.from)
          : null
      : null;
  const annotateWith = liveAnnotate && !sameRates(liveAnnotate, basis.copy) ? liveAnnotate : null;

  const note = (() => {
    const pickedLive = liveById(picked);
    // The card-has-changed note applies to exactly one situation: the trip was
    // saved in defaultRate on a card, and that SAME card's figures have moved
    // since. A trip saved on its own figures has no card in its history, and a
    // different card was never the trip's basis, so in both cases picking a card
    // is just choosing a new basis — nothing has drifted. The comparison is
    // against the trip's SAVED figures, never the working copy, which holds
    // whatever the user has edited in this sitting.
    const driftable =
      ctx.pricingMode === 'defaultRate' &&
      !!ctx.originRateCardId &&
      `${picked}` === `${ctx.originRateCardId}`;
    if (
      !vehicleChanged &&
      mode === 'defaultRate' &&
      picked &&
      pickedLive &&
      driftable &&
      !sameRates(pickedLive, ctx.rateConfig)
    ) {
      return pickTouched
        ? {
            sev: 'warning',
            text: "These are the rate card's figures as they stand today, which differ from the ones the trip was last saved with. Saving will price the trip on them.",
            undo: true,
          }
        : {
            sev: 'warning',
            text: 'This rate card has changed since the trip was last saved on it. The trip still carries the figures it was last saved with, so no rate card is shown as selected. Pick it again to take the card’s current figures.',
          };
    }
    if (vehicleChanged && mode === 'defaultRate' && picked) {
      return {
        sev: 'warning',
        text: `Saving records ${vehicleLabel(vehicle)} on the trip and prices it on this rate card.`,
        undo: true,
      };
    }
    // Moving a trip off its own figures onto a card, or from one card to
    // another. The fare moves because a card was chosen, so say so plainly —
    // there is nothing here to warn about.
    if (!vehicleChanged && mode === 'defaultRate' && picked && !driftable) {
      return {
        sev: 'info',
        text:
          ctx.pricingMode === 'defaultRate'
            ? 'Saving prices the trip on this rate card instead of the one it was last saved on.'
            : 'This trip was priced on its own figures. Saving prices it on this rate card instead.',
        undo: true,
      };
    }
    if (vehicleChanged && mode === 'defaultRate' && !picked && cardsRequested && !cardsLoading) {
      return {
        sev: 'info',
        text: `The vehicle has changed, so the rate card the trip was priced on no longer applies. Pick a rate card for ${vehicleLabel(vehicle)} — the trip keeps its last saved figures until you do.`,
      };
    }
    if (vehicleChanged && !hasCards && mode === 'customRate' && cardsRequested && !cardsLoading) {
      return {
        sev: 'warning',
        text: `No rate cards are configured for ${vehicleLabel(vehicle)} in this city, so these rates are held on the trip alone.`,
      };
    }
    if (mode === 'defaultRate' && !picked && cardsRequested && !cardsLoading) {
      return {
        sev: 'info',
        text: 'This trip is still priced on its own figures. Choose a rate card to price it on one — the fare will not change until you do.',
      };
    }
    if (mode === 'customRate' && !vehicleChanged && openingBasis?.diverged && !editedDirty) {
      return {
        sev: 'warning',
        text: 'This trip’s rate card has changed since the trip was last saved on it. The figures below are the ones the trip was priced on and no longer match any rate card, so the trip is on Edited Rates — saving keeps them as its own. Switch to Rate Cards and pick the card to take its current figures instead.',
      };
    }
    if (mode === 'customRate' && !ctx.rateCardId && cardsRequested && !cardsLoading && !hasCards) {
      return {
        sev: 'warning',
        text: `No rate cards are configured for ${dutyLabel} in this city and car type, so these rates are held on the trip alone.`,
      };
    }
    if (
      mode === 'customRate' &&
      !ctx.originRateCardId &&
      hasCards &&
      !editedDirty &&
      !openingBasis?.diverged
    ) {
      return {
        sev: 'info',
        text: 'This trip is priced on its own figures, not on a rate card. Switch to Rate Cards to price it on one of the corporate’s cards instead.',
      };
    }
    return null;
  })();

  const ratesFrom = (() => {
    if (mode === 'customRate') {
      if (editedDirty)
        return adjustedFrom
          ? `adjusted from ${adjustedFrom}, held on this trip`
          : 'held on this trip, edited now';
      if (openingBasis?.diverged && !vehicleChanged)
        return 'the figures this trip was last saved with — its rate card has since changed';
      return 'held on this trip';
    }
    if (vehicleChanged && !picked)
      return `the ${vehicleLabel(ctx.carTypeId)} figures the trip was last saved with — pick a rate card for ${vehicleLabel(vehicle)} to replace them`;
    if (!picked) return 'the figures this trip was last saved with';
    if (!liveById(picked)) return "the rate card, from the trip's copy";
    return pickTouched
      ? "the rate card's figures as they stand today"
      : 'the figures this trip was last saved with';
  })();

  // A picked card needs no explanation: the fare below already says what it
  // came to, and the line describing what saving would do only restated the
  // obvious in a modal that has none to spare. Withdrawn, not deleted — the
  // original is here if it is wanted back:
  //
  //   `Saving prices ${multiDay ? 'every day of ' : ''}this trip on the chosen
  //    rate card and stores its id, exactly as if it had been chosen at booking.`
  //
  // The other two earn their room: one says nothing has happened yet, the
  // other what Edited Rates means.
  const hint =
    mode === 'defaultRate'
      ? picked
        ? ''
        : 'Choose a rate card to price this trip on it. Nothing changes until you do.'
      : 'These figures are held on this trip only and no rate card id is stored. Switch to Rate Cards to price on one of the corporate’s rate cards instead.';

  const odoStart = num(facts.odometerStart);
  const odoEnd = num(facts.odometerEnd);
  const odoOk = odoStart > 0 && odoEnd > 0 && odoEnd >= odoStart;
  const odoDist = odoOk ? odoEnd - odoStart : null;
  const travelHrs = (() => {
    const s = new Date(facts.tripStartTime).getTime();
    const e = new Date(facts.tripEndTime).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e >= s ? (e - s) / 36e5 : null;
  })();
  const billedDistance = preview?.distance ?? ctx.distance;
  const total = preview?.pricing?.totalAmount;
  const breakup = useMemo(() => fareRows(preview?.pricing), [preview]);
  const delta = total !== undefined && total !== null ? Number(total) - savedFare : null;

  // Multi-day summary strip: totals for the slip only — billing is per day.
  const dayList = ctx?.multiDay?.days || [];
  const previewDays = preview?.days || [];
  const dayTotals = (() => {
    const src = previewDays.length ? previewDays : dayList;
    return src.reduce(
      (acc, d) => ({
        hours: acc.hours + num(d?.totalHours),
        km: acc.km + num(d?.totalDistance),
      }),
      { hours: 0, km: 0 },
    );
  })();

  const hasRemarks = remarks.trim().length > 0;
  const blocked =
    saving ||
    (multiDay && !hasDayLog) ||
    needsPick ||
    !hasRemarks ||
    !!previewError ||
    (mode === 'defaultRate' && (cardsLoading || !cardsRequested)) ||
    !(pricingMoved || factsChanged());
  const why =
    multiDay && !hasDayLog
      ? 'The day log needs the updated backend; this trip cannot be edited from here yet.'
      : basisHeld
        ? `The figures below are the ${labelForDuty(bookedDutyType)} rates this trip was priced on. Pick a ${labelForDuty(dutyType)} rate card to price it as one.`
        : needsPick
          ? 'Choose a rate card before saving, or switch back to Edited Rates.'
          : previewError
            ? // Stated in full beside the fare; repeating it here puts the same
              // sentence on screen twice and reads as two separate problems.
              'This edit cannot be priced — see the note beside the fare.'
            : !hasRemarks
              ? 'Correction remarks are required before Update.'
              : !(pricingMoved || factsChanged())
                ? 'Nothing has changed yet.'
                : '';

  const fields = copy ? fieldsFor(dutyType, copy).filter((f) => fieldVisible(f, copy)) : [];
  // Folded by default, but never folded over the operator's own work: figures
  // typed by hand, or a basis waiting to be chosen, are the thing being
  // corrected and must be in view without a click to find them.
  const ratesOpen = showRates || mode === 'customRate' || basisHeld;

  return (
    <Dialog open={!!open} onClose={() => handleClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h6" component="span" className="fw600">
          Edit Duty Slip
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: mode === 'customRate' ? '#fff8e8' : '#eaf6eb',
            color: mode === 'customRate' ? '#412402' : '#1e5a20',
          }}
        >
          {MODE_LABEL[mode]}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={() => handleClose(false)}
          sx={{ ml: 'auto' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={COMPACT_SX}>
        <Grid container spacing={1.5}>
          {/* ---------- Pricing ---------- */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary">
              Pricing
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                what the trip was, then how it is priced
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            {multiDay ? (
              <TextField
                label="Duty Type"
                fullWidth
                size="small"
                value={dutyLabel}
                disabled
                helperText="a multi-day rental keeps its duty type"
              />
            ) : (
              <TextField
                select
                label="Duty Type"
                fullWidth
                size="small"
                value={dutyType}
                onChange={(e) => onDutyTypeChange(e.target.value)}
                helperText={
                  dutyTypeChanged
                    ? `was ${labelForDuty(bookedDutyType)} — the trip becomes this`
                    : 'as booked'
                }
              >
                {dutyTypeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Vehicle Category"
              fullWidth
              size="small"
              value={vehicle ?? ''}
              onChange={(e) => onVehicleChange(e.target.value)}
              helperText={
                vehicleChanged
                  ? `changed from ${vehicleLabel(ctx.carTypeId)}; saving records this vehicle on the trip`
                  : 'the vehicle that ran the trip'
              }
            >
              {vehicleOptions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              label="Pricing Mode"
              fullWidth
              size="small"
              value={mode}
              disabled={modeOptions.length === 1}
              onChange={(e) => onModeChange(e.target.value)}
            >
              {modeOptions.map((m) => (
                <MenuItem key={m} value={m}>
                  {MODE_LABEL[m]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            {mode === 'customRate' ? (
              <TextField
                label="Rate Card"
                fullWidth
                size="small"
                value="Custom Rate Card"
                disabled
              />
            ) : cardsLoading ? (
              <TextField
                label="Rate Card"
                fullWidth
                size="small"
                value="Loading rate cards…"
                disabled
              />
            ) : (
              <TextField
                select
                label="Rate Card"
                fullWidth
                size="small"
                value={cardMatchesTrip() ? picked : ''}
                onChange={(e) => onPickCard(e.target.value)}
                // displayEmpty draws the placeholder while nothing is picked,
                // so the label has to float from the start or the two overlap.
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? (
                      cardLabel(liveById(v))
                    ) : (
                      <span style={{ color: '#969696' }}>Pick a Rate Card</span>
                    ),
                }}
              >
                {cardList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {cardLabel(c)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          {note && (
            <Grid item xs={12}>
              {/* "Undo this" and "Undo my changes" (below) both called the same
                  function, and neither matched its label: they reverted the
                  basis but not the duty type, so undoing a conversion left the
                  trip as the new duty type with no card — the held state, with
                  Update dead. Two identical controls with different names, one
                  of them sitting under a message about the conversion, read as
                  three different scopes when there were two. Withdrawn until
                  each has a scope worth naming; Reset All Changes still
                  restores everything the backend sent. */}
              <Alert severity={note.sev}>{note.text}</Alert>
            </Grid>
          )}
          {!!hint && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            </Grid>
          )}

          {/* ---------- Rate figures ---------- */}
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid #e8e8e8',
                borderRadius: 1.5,
                px: 1.5,
                pt: 1.25,
                pb: 0.5,
                bgcolor: '#f9fafc',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setShowRates((s) => !s)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowRates((s) => !s)}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.5,
                  mb: ratesOpen ? 1 : 0,
                  flexWrap: 'wrap',
                  cursor: 'pointer',
                }}
              >
                <Typography variant="body2" className="fw600 colorPrimary">
                  Rate figures
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ratesFrom}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ ml: 'auto', color: '#1976d2', fontWeight: 600 }}
                >
                  {ratesOpen ? 'Hide' : `Show ${fields.length} figures`}
                </Typography>
                {/* Withdrawn with "Undo this" above — same function, same
                    scope, different name. See the note beside the alert. */}
              </Box>
              <Collapse in={ratesOpen}>
                <Grid container spacing={1.5}>
                  {fields.map((f) => {
                    const value = getAt(copy, f.path);
                    const other = annotateWith ? getAt(annotateWith, f.path) : undefined;
                    const moved =
                      annotateWith !== null &&
                      other !== undefined &&
                      `${value ?? ''}` !== `${other ?? ''}` &&
                      !(
                        Number.isFinite(Number(value)) &&
                        Number.isFinite(Number(other)) &&
                        Number(value) === Number(other)
                      );
                    return (
                      <Grid item xs={12} sm={6} md={3} key={f.path.join('.')}>
                        {f.select ? (
                          <TextField
                            select
                            label={f.label}
                            fullWidth
                            size="small"
                            value={value ?? ''}
                            disabled={stale}
                            sx={RATE_FIELD_SX}
                            onChange={(e) => setRate(f.path, e.target.value)}
                            helperText={
                              moved
                                ? `rate card now: ${f.select.find(([k]) => k === other)?.[1] || other}`
                                : ' '
                            }
                          >
                            {f.select.map(([k, label]) => (
                              <MenuItem key={k} value={k}>
                                {label}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            label={f.label}
                            fullWidth
                            size="small"
                            type="number"
                            value={value ?? ''}
                            disabled={stale || f.fixed}
                            sx={RATE_FIELD_SX}
                            onChange={(e) => setRate(f.path, e.target.value, true)}
                            InputProps={
                              f.unit
                                ? {
                                    endAdornment: (
                                      <InputAdornment position="end">{f.unit}</InputAdornment>
                                    ),
                                  }
                                : undefined
                            }
                            helperText={moved ? `rate card now: ${other}` : ' '}
                            FormHelperTextProps={{
                              sx: { color: '#8a6a1f', m: 0 },
                            }}
                          />
                        )}
                      </Grid>
                    );
                  })}
                  {!fields.length && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        No rate figures on this trip yet.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Collapse>
            </Box>
          </Grid>

          {/* ---------- Day log (multi-day) ---------- */}
          {multiDay && (
            <Grid item xs={12}>
              <Typography variant="body2" className="fw600 colorPrimary">
                Day log
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {dayList.length
                    ? `${dayList.length} days · each priced on its own allowance · every day can be corrected`
                    : ''}
                </Typography>
              </Typography>
              {hasDayLog ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '4px 22px',
                      flexWrap: 'wrap',
                      fontSize: 12.5,
                      color: '#969696',
                      px: 1.5,
                      py: 1,
                      border: '1px dashed #c9c9c9',
                      borderRadius: 1,
                      mt: 1,
                    }}
                  >
                    <span>
                      Hours{' '}
                      <b style={{ color: '#282828', fontWeight: 500 }}>
                        {dayTotals.hours.toFixed(1)}
                      </b>
                    </span>
                    <span>
                      Distance{' '}
                      <b style={{ color: '#282828', fontWeight: 500 }}>
                        {dayTotals.km.toFixed(0)} km
                      </b>
                    </span>
                    <span>totals for the slip only — billing is per day, below</span>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <DayLog
                      days={dayList}
                      edits={dayEdits}
                      onEdit={setDayField}
                      priced={previewDays}
                      disabled={stale}
                    />
                  </Box>
                </>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  The day log needs the updated backend (the trip details response does not carry it
                  yet). Until then this multi-day rental cannot be edited from here.
                </Alert>
              )}
            </Grid>
          )}

          {/* ---------- Trip details ---------- */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary">
              {multiDay ? 'Trip charges' : 'Trip details'}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {multiDay
                  ? 'once per trip, not per day'
                  : 'measured by the system, correct if wrong'}
              </Typography>
            </Typography>
          </Grid>
          {!multiDay && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Trip Start Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={facts.tripStartTime}
                  onChange={(e) => setFact('tripStartTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="driver pressed Start"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Trip End Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={facts.tripEndTime}
                  onChange={(e) => setFact('tripEndTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="driver pressed End"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Actual Travel Time"
                  fullWidth
                  size="small"
                  value={travelHrs === null ? '' : travelHrs.toFixed(2)}
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hr</InputAdornment>,
                  }}
                  helperText="end − start"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Start Odometer"
                  type="number"
                  fullWidth
                  size="small"
                  value={facts.odometerStart}
                  onChange={(e) => setFact('odometerStart', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="End Odometer"
                  type="number"
                  fullWidth
                  size="small"
                  value={facts.odometerEnd}
                  onChange={(e) => setFact('odometerEnd', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Odometer Distance"
                  fullWidth
                  size="small"
                  value={odoDist === null ? '—' : odoDist}
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                  }}
                  helperText="end − start odometer"
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Garage to Pickup"
              type="number"
              fullWidth
              size="small"
              value={facts.garageToPickup}
              onChange={(e) => setFact('garageToPickup', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Drop to Garage"
              type="number"
              fullWidth
              size="small"
              value={facts.dropToGarage}
              onChange={(e) => setFact('dropToGarage', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">km</InputAdornment>,
              }}
            />
          </Grid>
          {!multiDay && (
            <Grid item xs={12} sm={4}>
              <TextField
                label="Billed Distance"
                fullWidth
                size="small"
                value={billedDistance ?? ''}
                disabled
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
                helperText={`GPS trail ${ctx.gpsDistance ?? '—'} km`}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Tolls"
              type="number"
              fullWidth
              size="small"
              value={facts.tollCharges}
              onChange={(e) => setFact('tollCharges', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Parking Charges"
              type="number"
              fullWidth
              size="small"
              value={facts.parkingCharges}
              onChange={(e) => setFact('parkingCharges', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">₹</InputAdornment>,
              }}
            />
          </Grid>
          {!multiDay && (
            <Grid item xs={12} sm={4}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  height: '100%',
                  px: 1.5,
                  border: '1px dashed #c9c9c9',
                  borderRadius: 1,
                }}
              >
                <FormLabel sx={{ fontSize: 12 }}>Bill on</FormLabel>
                <RadioGroup
                  row
                  value={facts.distanceSource}
                  onChange={(e) => setFact('distanceSource', e.target.value)}
                >
                  <FormControlLabel value="gps" control={<Radio size="small" />} label="GPS" />
                  <FormControlLabel
                    value="odometer"
                    control={<Radio size="small" />}
                    label="Odometer"
                    disabled={!odoOk}
                  />
                </RadioGroup>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip Start Location"
              fullWidth
              size="small"
              value={facts.tripStartLocationManual}
              onChange={(e) => setFact('tripStartLocationManual', e.target.value)}
              placeholder="label for the duty slip, optional"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trip End Location"
              fullWidth
              size="small"
              value={facts.tripEndLocationManual}
              onChange={(e) => setFact('tripEndLocationManual', e.target.value)}
              placeholder="label for the duty slip, optional"
            />
          </Grid>

          {/* ---------- Fare ---------- */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary">
              Fare
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                recomputed as you type; expand for the break-up
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px solid #e8e8e8',
                borderRadius: 1.5,
                overflow: 'hidden',
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setShowBreakup((s) => !s)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowBreakup((s) => !s)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  bgcolor: '#4caf50',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                <Typography className="fw600">Total</Typography>
                <Typography variant="caption" sx={{ opacity: 0.92 }}>
                  {previewing
                    ? 'recalculating…'
                    : previewError
                      ? // The bar has room for a few words beside a figure, not
                        // for the reason. Saying it twice, once here truncated
                        // and once in full below, reads as two faults.
                        'cannot be priced'
                      : delta === null
                        ? ''
                        : delta === 0
                          ? 'unchanged from the saved fare'
                          : `${delta > 0 ? '+' : '−'}${fmt(Math.abs(delta))} vs saved fare`}
                </Typography>
                <Typography className="fw600" sx={{ ml: 'auto', fontSize: 18 }}>
                  {total !== undefined && total !== null
                    ? fmt(total)
                    : savedFare
                      ? fmt(savedFare)
                      : '—'}
                </Typography>
              </Box>
              {/* Outside the fold: a fare that could not be computed has no
                  break-up to expand, so a reason hidden behind a toggle is a
                  reason nobody reads. */}
              {!!previewError && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                  {previewError}
                </Alert>
              )}
              <Collapse in={showBreakup}>
                {breakup ? (
                  <Table size="small">
                    <TableBody>
                      {breakup.taxableRows.map((row) => (
                        <FareRow key={row.key} row={row} />
                      ))}
                      <FareRow
                        row={{ label: 'Taxable amount', amount: breakup.taxable }}
                        strong
                        ruled
                      />
                      {breakup.gst && <FareRow row={breakup.gst} />}
                      {breakup.reimbursements.map((row) => (
                        <FareRow key={row.key} row={row} />
                      ))}
                      {breakup.roundOff && <FareRow row={breakup.roundOff} />}
                      <FareRow row={{ label: 'Total', amount: breakup.total }} strong ruled />
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {previewError ? '' : 'No break-up yet.'}
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </Box>
          </Grid>

          {/* ---------- Audit ---------- */}
          <Grid item xs={12}>
            <Typography variant="body2" className="fw600 colorPrimary">
              Audit <span style={{ color: '#d32f2f' }}>*</span>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                required on every correction
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Correction remarks *"
              fullWidth
              multiline
              minRows={1}
              size="small"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 1,
          borderTop: '1px solid #e8e8e8',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', maxWidth: '56ch' }}>
          {why}
        </Typography>
        <CommonButton
          name="Reset all changes"
          btnSize="btnSm"
          outline
          disabled={!anythingChanged()}
          onClick={resetAll}
        />
        <CommonButton name="Cancel" btnSize="btnSm" outline onClick={() => handleClose(false)} />
        <CommonButton
          name={saving ? 'Updating…' : 'Update'}
          btnSize="btnSm"
          disabled={blocked}
          onClick={onUpdate}
        />
      </Box>
    </Dialog>
  );
};

export default DutySlipDialogV2;
